import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: 'left' | 'right' | 'bottom';
  children: React.ReactNode;
  /** Extra classes for the sheet panel (e.g. to tune the bottom-sheet body). */
  className?: string;
}

/** Slide-in panel for mobile drawers.
 *  - `left`/`right`: full-height side panels.
 *  - `bottom`: full-width sheet that slides up from the bottom edge, rounded
 *    top corners, max-height with internal scroll, respects the bottom
 *    safe-area inset. */
export function Sheet({ open, onOpenChange, side = 'left', children, className }: SheetProps) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const isBottom = side === 'bottom';

  return createPortal(
    // data-overlay marks this portaled tree so panels that listen for
    // outside-pointerdown (e.g. ResizableSidebar's collapse-on-tap) can skip
    // taps that land inside the sheet/backdrop and not tear it down.
    <div className="fixed inset-0 z-50" data-overlay>
      <div
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-150"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'flex flex-col bg-sidebar text-sidebar-foreground border-sidebar-border shadow-xl',
          isBottom
            ? 'absolute inset-x-0 bottom-0 max-h-[80vh] rounded-t-2xl border-t animate-in slide-in-from-bottom duration-200'
            : 'absolute top-0 bottom-0 w-72',
          side === 'left' && 'left-0 border-r animate-in slide-in-from-left duration-200',
          side === 'right' && 'right-0 border-l animate-in slide-in-from-right duration-200',
          className,
        )}
        style={isBottom ? { paddingBottom: 'env(safe-area-inset-bottom)' } : undefined}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
